import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import {
  FaMoneyBillWave,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaExternalLinkAlt,
  FaSpinner,
  FaWallet,
  FaShieldAlt,
  FaArrowRight,
  FaHistory,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaInfoCircle,
} from "react-icons/fa";

const Withdraw = () => {
  const { user } = useAuth();
  const [amount, setAmount] = useState("");
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawalSettings, setWithdrawalSettings] = useState({
    hasPinSet: false,
    withdrawalAddress: null,
  });
  const [focusedField, setFocusedField] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);

  const withdrawalFeePercent = 0.1; // 10% fee
  const minWithdrawal = 10;

  useEffect(() => {
    fetchWithdrawals();
    fetchWithdrawalSettings();
  }, []);

  const handleResendConfirmation = async (withdrawalId) => {
    setResendingEmail(withdrawalId);
    try {
      const response = await api.post(
        `/withdrawals/resend-confirmation/${withdrawalId}`,
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to resend confirmation email",
      );
    } finally {
      setResendingEmail(null);
    }
  };

  const fetchWithdrawals = async () => {
    try {
      const response = await api.get("/withdrawals/history");
      setWithdrawals(response.data.withdrawals);
      setLoading(false);
    } catch (error) {
      console.error("Failed to load withdrawal history");
      setLoading(false);
    }
  };

  const fetchWithdrawalSettings = async () => {
    try {
      const { data } = await api.get("/users/withdrawal-settings");
      setWithdrawalSettings(
        data.settings || { hasPinSet: false, withdrawalAddress: null },
      );
    } catch (error) {
      console.error("Failed to load withdrawal settings");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const withdrawAmount = parseFloat(amount);

    if (!withdrawAmount || withdrawAmount < minWithdrawal) {
      toast.error(`Minimum withdrawal amount is $${minWithdrawal}`);
      return;
    }

    // Fee is taken from within the withdrawal amount, not added on top
    if (withdrawAmount > user.walletBalance) {
      toast.error("Insufficient balance");
      return;
    }

    if (!withdrawalSettings.hasPinSet) {
      toast.error("Set your withdrawal PIN in Profile first");
      return;
    }

    if (!withdrawalSettings.withdrawalAddress) {
      toast.error("Set your withdrawal address in Profile first");
      return;
    }

    if (!/^\d{4,6}$/.test(pin)) {
      toast.error("Enter your 4-6 digit withdrawal PIN");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post("/withdrawals/request", {
        amount: withdrawAmount,
        pin,
      });

      toast.success(response.data.message);
      setAmount("");
      setPin("");
      fetchWithdrawals();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to request withdrawal",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <FaCheckCircle className="text-emerald-400" />;
      case "processing":
        return <FaSpinner className="text-cyan-400 animate-spin" />;
      case "awaiting_confirmation":
        return <FaClock className="text-amber-400" />;
      case "pending":
        return <FaClock className="text-amber-400" />;
      case "rejected":
        return <FaTimesCircle className="text-rose-400" />;
      default:
        return <FaClock className="text-slate-400" />;
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "completed":
        return "text-emerald-300 bg-emerald-500/15 border-emerald-500/30";
      case "processing":
        return "text-cyan-300 bg-cyan-500/15 border-cyan-500/30";
      case "awaiting_confirmation":
        return "text-amber-300 bg-amber-500/15 border-amber-500/30";
      case "pending":
        return "text-amber-300 bg-amber-500/15 border-amber-500/30";
      case "rejected":
        return "text-rose-300 bg-rose-500/15 border-rose-500/30";
      default:
        return "text-slate-300 bg-white/10 border-white/10";
    }
  };

  const withdrawalFee = amount ? parseFloat(amount) * withdrawalFeePercent : 0;
  const netAmount =
    amount ? (parseFloat(amount) - withdrawalFee).toFixed(2) : "0.00";

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-[60vh] gap-3">
        <div className="w-12 h-12 rounded-full border-[3px] border-cyan-400/30 border-t-cyan-400 animate-spin" />
        <p className="text-sm text-slate-400 animate-pulse">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-6">
      {/* Balance Hero Card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-500 via-pink-600 to-rose-600 p-5 shadow-xl shadow-purple-500/10">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 blur-xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <FaMoneyBillWave className="text-lg text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Withdraw USDT</h1>
              <p className="text-[11px] text-pink-100/80 font-medium">
                BEP20 Network
              </p>
            </div>
          </div>

          <div className="bg-white/15 backdrop-blur-xl rounded-xl px-4 py-3 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] text-pink-100 font-medium uppercase tracking-wider">
                  Available Balance
                </p>
                <p className="text-2xl font-extrabold text-white tracking-tight mt-0.5">
                  ${user?.walletBalance?.toFixed(2) || "0.00"}
                </p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                <FaWallet className="text-lg text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Security Status Pills */}
      <div className="flex gap-2">
        <div
          className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border ${
            withdrawalSettings.hasPinSet ?
              "border-emerald-500/20 bg-emerald-500/[0.06]"
            : "border-amber-500/20 bg-amber-500/[0.06]"
          }`}
        >
          <FaLock
            className={`text-xs ${withdrawalSettings.hasPinSet ? "text-emerald-400" : "text-amber-400"}`}
          />
          <span
            className={`text-xs font-semibold ${withdrawalSettings.hasPinSet ? "text-emerald-300" : "text-amber-300"}`}
          >
            PIN {withdrawalSettings.hasPinSet ? "Set" : "Not Set"}
          </span>
        </div>
        <div
          className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border ${
            withdrawalSettings.withdrawalAddress ?
              "border-emerald-500/20 bg-emerald-500/[0.06]"
            : "border-amber-500/20 bg-amber-500/[0.06]"
          }`}
        >
          <FaShieldAlt
            className={`text-xs ${withdrawalSettings.withdrawalAddress ? "text-emerald-400" : "text-amber-400"}`}
          />
          <span
            className={`text-xs font-semibold ${withdrawalSettings.withdrawalAddress ? "text-emerald-300" : "text-amber-300"}`}
          >
            Address{" "}
            {withdrawalSettings.withdrawalAddress ? "Locked" : "Not Set"}
          </span>
        </div>
      </div>

      {/* Withdrawal Form */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <FaMoneyBillWave className="text-cyan-400 text-xs" />
          Request Withdrawal
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          {/* Amount */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
              Amount (USDT)
            </label>
            <div
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${
                focusedField === "amount" ?
                  "border-cyan-400/60 bg-white/[0.07] shadow-[0_0_20px_rgba(34,211,238,0.1)]"
                : "border-white/10 bg-white/[0.04]"
              }`}
            >
              <span
                className={`text-sm font-bold transition-colors ${
                  focusedField === "amount" ? "text-cyan-400" : "text-slate-500"
                }`}
              >
                $
              </span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onFocus={() => setFocusedField("amount")}
                onBlur={() => setFocusedField(null)}
                placeholder={`Min ${minWithdrawal}`}
                step="0.01"
                min={minWithdrawal}
                className="flex-1 bg-transparent text-white placeholder:text-slate-500 outline-none text-sm font-semibold"
                autoComplete="off"
                required
              />
            </div>
          </div>

          {/* Address (read-only) */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
              Withdrawal Address
            </label>
            {withdrawalSettings.withdrawalAddress ?
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.04]">
                <FaLock className="text-xs text-slate-500 flex-shrink-0" />
                <span className="text-sm text-slate-300 font-mono truncate">
                  {withdrawalSettings.withdrawalAddress}
                </span>
              </div>
            : <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-amber-400/20 bg-amber-500/[0.06]">
                <FaExclamationTriangle className="text-xs text-amber-400 flex-shrink-0" />
                <span className="text-xs text-amber-300">
                  Set address in Profile first
                </span>
              </div>
            }
          </div>

          {/* PIN */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
              Withdrawal PIN
            </label>
            <div
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${
                focusedField === "pin" ?
                  "border-cyan-400/60 bg-white/[0.07] shadow-[0_0_20px_rgba(34,211,238,0.1)]"
                : "border-white/10 bg-white/[0.04]"
              }`}
            >
              <FaLock
                className={`text-xs transition-colors ${
                  focusedField === "pin" ? "text-cyan-400" : "text-slate-500"
                }`}
              />
              <input
                type={showPin ? "text" : "password"}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onFocus={() => setFocusedField("pin")}
                onBlur={() => setFocusedField(null)}
                placeholder="4-6 digit PIN"
                className="flex-1 bg-transparent text-white placeholder:text-slate-500 outline-none text-sm font-semibold tracking-widest"
                autoComplete="new-password"
                required
                maxLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="text-slate-500 hover:text-slate-300 transition-colors touch-manipulation p-1"
                tabIndex={-1}
              >
                {showPin ?
                  <FaEyeSlash className="text-sm" />
                : <FaEye className="text-sm" />}
              </button>
            </div>
          </div>

          {/* Fee Breakdown */}
          {amount && parseFloat(amount) >= minWithdrawal && (
            <div className="rounded-xl bg-white/[0.04] border border-white/[0.08] p-3.5">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-400">Amount</span>
                  <span className="text-sm font-semibold text-white">
                    ${parseFloat(amount).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-400">Fee (10%)</span>
                  <span className="text-sm font-semibold text-rose-400">
                    -${withdrawalFee.toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-white/10 pt-2 flex justify-between items-center">
                  <span className="text-xs font-semibold text-white">
                    You Receive
                  </span>
                  <span className="text-base font-bold text-emerald-400">
                    ${netAmount} USDT
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={
              submitting ||
              !withdrawalSettings.hasPinSet ||
              !withdrawalSettings.withdrawalAddress
            }
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/20 active:scale-[0.97] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none touch-manipulation text-sm"
          >
            {submitting ?
              <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <>
                Request Withdrawal
                <FaArrowRight className="text-xs" />
              </>
            }
          </button>
        </form>
      </div>

      {/* Quick Info */}
      <div className="flex gap-3 overflow-x-auto scrollbar-hide">
        {[
          {
            label: "Min",
            value: `$${minWithdrawal}`,
            icon: FaMoneyBillWave,
            color: "text-cyan-400",
          },
          {
            label: "Fee",
            value: `$${withdrawalFee}`,
            icon: FaInfoCircle,
            color: "text-rose-400",
          },
          {
            label: "Network",
            value: "BEP20",
            icon: FaShieldAlt,
            color: "text-violet-400",
          },
          {
            label: "Time",
            value: "24-48h",
            icon: FaClock,
            color: "text-amber-400",
          },
        ].map((item, i) => {
          const Icon = item.icon;
          return (
            <div
              key={i}
              className="flex-shrink-0 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06]"
            >
              <Icon className={`text-xs ${item.color}`} />
              <div>
                <p className="text-[10px] text-slate-500 font-medium">
                  {item.label}
                </p>
                <p className="text-xs font-bold text-white">{item.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Withdrawal History */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <FaHistory className="text-cyan-400 text-xs" />
          Withdrawal History
        </h2>

        {withdrawals.length > 0 ?
          <div className="space-y-2.5">
            {withdrawals.map((w) => (
              <div
                key={w._id}
                className="flex items-center gap-3 px-3.5 py-3 rounded-xl bg-white/[0.03] border border-white/[0.05] active:bg-white/[0.06] transition-colors touch-manipulation"
              >
                {/* Status icon */}
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    w.status === "completed" ? "bg-emerald-500/15"
                    : w.status === "processing" ? "bg-cyan-500/15"
                    : w.status === "awaiting_confirmation" ? "bg-amber-500/15"
                    : w.status === "pending" ? "bg-amber-500/15"
                    : "bg-rose-500/15"
                  }`}
                >
                  {getStatusIcon(w.status)}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-sm font-bold text-white">
                      ${w.amount?.toFixed(2)}
                    </span>
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize ${getStatusStyle(w.status)}`}
                    >
                      {w.status === "awaiting_confirmation" ?
                        "Awaiting Email"
                      : w.status === "pending" && w.confirmationToken ?
                        "Awaiting Email"
                      : w.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[11px] text-slate-500">
                      <span>{new Date(w.createdAt).toLocaleDateString()}</span>
                      <span className="w-0.5 h-0.5 bg-slate-600 rounded-full" />
                      <span>Fee: ${w.fee?.toFixed(2)}</span>
                    </div>
                    <span className="text-xs font-semibold text-emerald-400">
                      +${w.netAmount?.toFixed(2)}
                    </span>
                  </div>
                  {/* Address row */}
                  <div className="mt-1 flex items-center gap-1">
                    <span className="text-[10px] text-slate-600 font-mono truncate">
                      {w.withdrawalAddress?.substring(0, 10)}...
                      {w.withdrawalAddress?.slice(-6)}
                    </span>
                    {w.status === "completed" && w.txHash && (
                      <a
                        href={`https://bscscan.com/tx/${w.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-400 hover:text-cyan-300 ml-auto"
                      >
                        <FaExternalLinkAlt className="text-[10px]" />
                      </a>
                    )}
                  </div>

                  {/* Awaiting confirmation action */}
                  {(w.status === "awaiting_confirmation" ||
                    (w.status === "pending" && w.confirmationToken)) && (
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={() => handleResendConfirmation(w._id)}
                        disabled={resendingEmail === w._id}
                        className="text-[10px] px-2 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-md border border-amber-500/30 transition-colors disabled:opacity-50"
                      >
                        {resendingEmail === w._id ?
                          "Sending..."
                        : "Resend Email"}
                      </button>
                      <span className="text-[10px] text-amber-400">
                        ⚠️ Check your email to confirm
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        : <div className="text-center py-10">
            <div className="w-14 h-14 rounded-full bg-white/[0.05] flex items-center justify-center mx-auto mb-3 border border-white/[0.08]">
              <FaHistory className="text-xl text-slate-500" />
            </div>
            <p className="text-sm font-medium text-slate-400">
              No withdrawals yet
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Your history will appear here
            </p>
          </div>
        }
      </div>
    </div>
  );
};

export default Withdraw;
